import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { sendChat, getJournalPrompts, getJournalStreak, saveReflection, emailReflectionSummary } from "../lib/api";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Moon, ArrowRight, Sparkles, Flame, RefreshCw, Mail, Loader2, Check } from "lucide-react";

/* Nightly Reflection Journal \u2014 3 quick prompts the AI weaves into pattern memory.
   Lightweight; uses the existing /api/coach/chat under the hood as a reflective summary. */

const PROMPTS = [
  { q: "How did your body feel today?",            placeholder: "Energy, soreness, cramps, calm \u2026" },
  { q: "What drained you?",                         placeholder: "A meeting, a person, a habit, news \u2026" },
  { q: "What softened you?",                        placeholder: "A walk, a meal, a song, someone \u2026" },
];

export default function ReflectionSheet({ open, onClose, profile }) {
  const [prompts, setPrompts] = useState(PROMPTS);
  const [answers, setAnswers] = useState(["", "", ""]);
  const [submitting, setSubmitting] = useState(false);
  const [reflection, setReflection] = useState(null);
  const [streak, setStreak] = useState({ streak: 0, longest: 0, today_logged: false });
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    if (!open) return;
    getJournalPrompts("any").then((arr) => {
      if (arr && arr.length) {
        const first3 = arr.slice(0, 3).map((p) => ({ q: p.prompt, placeholder: "Write a sentence or two…" }));
        setPrompts(first3);
        setAnswers(first3.map(() => ""));
      }
    }).catch(() => {});
    getJournalStreak().then(setStreak).catch(() => {});
  }, [open]);

  const reset = () => {
    setAnswers(prompts.map(() => ""));
    setReflection(null);
    setShowEmailInput(false);
    setEmailValue("");
    setEmailSent(false);
  };

  const close = () => {
    reset();
    onClose && onClose();
  };

  const reroll = async () => {
    try {
      const arr = await getJournalPrompts("any");
      if (!arr || !arr.length) return;
      const shuffled = arr.slice().sort(() => Math.random() - 0.5).slice(0, 3).map((p) => ({ q: p.prompt, placeholder: "Write a sentence or two…" }));
      setPrompts(shuffled);
      setAnswers(shuffled.map(() => ""));
    } catch { /* */ }
  };

  const submit = async () => {
    if (answers.every((a) => !a.trim())) {
      toast.error("Share at least one note so Knya can listen.");
      return;
    }
    setSubmitting(true);
    try {
      const summary = prompts.map((p, i) => `${p.q}\n${answers[i] || "(blank)"}`).join("\n\n");
      const userMsg = `Nightly reflection:\n\n${summary}\n\nReply with one short, warm paragraph (max 4 sentences) reflecting what you notice in my day. Do not list. Do not use medical advice. End with one tiny ritual to try tonight.`;
      const res = await sendChat(userMsg, []);
      const reply = res.reply || "I'm here. Thank you for sharing tonight.";
      setReflection(reply);
      // Persist + refresh streak (best-effort)
      try {
        const persisted = await saveReflection({
          answers: prompts.map((p, i) => ({ q: p.q, a: answers[i] || "" })),
          reflection_text: reply,
          send_email: false,
        });
        if (persisted?.streak) setStreak((s) => ({ ...s, streak: persisted.streak.streak ?? s.streak, today_logged: true }));
      } catch { /* */ }
      getJournalStreak().then(setStreak).catch(() => {});
      // Pre-fill email if profile has one
      if (profile?.email && !profile?.is_guest) setEmailValue(profile.email);
    } catch {
      toast.error("Couldn't reach Knya. Try again.");
    }
    setSubmitting(false);
  };

  const sendEmail = async () => {
    const target = (emailValue || profile?.email || "").trim();
    if (!target) {
      setShowEmailInput(true);
      return;
    }
    setEmailSending(true);
    try {
      await emailReflectionSummary({
        email: target,
        reflection_text: reflection || "",
        answers: prompts.map((p, i) => ({ q: p.q, a: answers[i] || "" })),
      });
      setEmailSent(true);
      toast.success("Sent. Look for a soft note from Knya in your inbox.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Couldn't send the email.");
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <Sheet open={!!open} onOpenChange={(v) => !v && close()}>
      <SheetContent
        side="bottom"
        className="rounded-t-[32px] max-w-[480px] mx-auto bg-card border-t-0 pb-8"
        data-testid="reflection-sheet"
      >
        <SheetHeader className="text-left">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2.5">
              <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-secondary text-secondary-foreground">
                <Moon size={16} />
              </span>
              <p className="text-[10px] uppercase tracking-wider font-extrabold text-primary">Nightly reflection</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex items-center gap-1 rounded-full bg-blush text-primary px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider" data-testid="journal-streak">
                <Flame size={10} /> {streak.streak}d
              </div>
              <button onClick={reroll} className="text-muted-foreground hover:text-foreground" aria-label="New prompts" data-testid="journal-reroll">
                <RefreshCw size={14} />
              </button>
            </div>
          </div>
          <SheetTitle className="text-[26px] font-extrabold tracking-tight">
            How was today, really?
          </SheetTitle>
          <SheetDescription className="text-[13px] text-muted-foreground font-medium">
            Three soft prompts. Knya listens, never judges. Streak: <span className="font-extrabold text-foreground">{streak.streak}</span> day{streak.streak === 1 ? "" : "s"} · Longest: <span className="font-extrabold text-foreground">{streak.longest}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-5 space-y-4 max-h-[55vh] overflow-y-auto scrollbar-thin pr-1">
          {!reflection && prompts.map((p, idx) => (
            <div key={idx}>
              <p className="text-[16px] leading-tight font-extrabold tracking-tight">{p.q}</p>
              <Textarea
                placeholder={p.placeholder}
                value={answers[idx]}
                onChange={(e) => {
                  const next = [...answers]; next[idx] = e.target.value; setAnswers(next);
                }}
                className="mt-2 min-h-[60px] bg-[hsl(var(--ivory))] rounded-2xl border-[hsl(var(--border))]"
                data-testid={`reflection-prompt-${idx}`}
              />
            </div>
          ))}

          {reflection && (
            <motion.div
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="surface-plum editorial-shadow rounded-[24px] p-5"
              data-testid="reflection-result"
            >
              <div className="inline-flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-[hsl(var(--gold))]" />
                <p className="text-[10px] uppercase tracking-[0.28em] font-semibold opacity-70">Knya reflects</p>
              </div>
              <p className="font-editorial text-[20px] leading-snug">{reflection}</p>

              {/* Email me this reflection — Resend transactional flow */}
              <div className="mt-4 pt-4 border-t border-white/15" data-testid="reflection-email-block">
                {!showEmailInput && !emailSent && (
                  <button
                    type="button"
                    onClick={() => {
                      if (profile?.email && !profile?.is_guest) {
                        setEmailValue(profile.email);
                        sendEmail();
                      } else {
                        setShowEmailInput(true);
                      }
                    }}
                    className="inline-flex items-center gap-2 rounded-full bg-white/12 hover:bg-white/20 transition px-4 py-2 text-[12px] font-bold"
                    data-testid="reflection-email-trigger"
                  >
                    <Mail size={13} /> Email me this reflection
                  </button>
                )}
                {showEmailInput && !emailSent && (
                  <div className="flex items-center gap-2" data-testid="reflection-email-input-row">
                    <Input
                      type="email"
                      value={emailValue}
                      onChange={(e) => setEmailValue(e.target.value)}
                      placeholder="you@example.com"
                      className="flex-1 h-10 rounded-full bg-white/12 border-white/20 placeholder:text-white/45 text-white"
                      data-testid="reflection-email-input"
                    />
                    <button
                      onClick={sendEmail}
                      disabled={emailSending || !emailValue.trim()}
                      className="inline-flex items-center justify-center h-10 px-4 rounded-full bg-primary text-primary-foreground font-bold text-[12px] disabled:opacity-50"
                      data-testid="reflection-email-send"
                    >
                      {emailSending ? <Loader2 size={14} className="animate-spin" /> : <>Send <ArrowRight size={12} className="ml-1" /></>}
                    </button>
                  </div>
                )}
                {emailSent && (
                  <p className="text-[12px] inline-flex items-center gap-1.5 opacity-85" data-testid="reflection-email-sent">
                    <Check size={13} /> Sent to <span className="font-bold">{emailValue}</span>
                  </p>
                )}
              </div>
            </motion.div>
          )}
        </div>

        <div className="mt-5 flex gap-2">
          {!reflection ? (
            <>
              <button
                onClick={close}
                className="btn-pill-cream flex-1"
                data-testid="reflection-cancel"
              >
                Not tonight
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="btn-pill-dark flex-1"
                data-testid="reflection-submit"
              >
                {submitting ? "Listening…" : <>Share with Knya <ArrowRight size={14} /></>}
              </button>
            </>
          ) : (
            <button
              onClick={close}
              className="btn-pill-dark flex-1"
              data-testid="reflection-close"
            >
              Goodnight
            </button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
